import React, { useMemo, useState, useCallback } from "react";
import { MobileOutlined, TabletOutlined, LaptopOutlined, DesktopOutlined } from "@ant-design/icons";
import FontSizeField from "./FontSizeField";
import { typography, textColors } from "../../../design-system/tokens";
import InfoIcon from "../InfoIcon";
import ScreenSizeModeSelector from "./ScreenSizeModeSelector";

function deriveTwoMode(values) {
  const xs = values?.extra_small;
  const s = values?.small ?? xs;
  const m = values?.medium;
  const l = values?.large ?? m;
  const xl = values?.extra_large ?? l;
  const xsEqual = Number(xs) === Number(s);
  const mEqual = Number(m) === Number(l) && Number(l) === Number(xl);
  return { isTwo: Boolean(xsEqual && mEqual), vMobile: xs ?? s ?? m, vDesktop: m ?? l ?? xl };
}

function allEqual(values) {
  const keys = ["extra_small", "small", "medium", "large", "extra_large"];
  const first = values?.extra_small;
  if (first === undefined || first === null) return false;
  return keys.every((k) => Number(values?.[k]) === Number(first));
}

export default function ByLayoutFontSizeField({ label, help, value, onChange, infoText }) {
  const { isTwo, vMobile, vDesktop } = useMemo(() => deriveTwoMode(value || {}), [value]);
  const [mode, setMode] = useState(allEqual(value || {}) ? "one" : isTwo ? "two" : "all");

  const setUnified = useCallback((px) => {
    onChange({
      extra_small: px,
      small: px,
      medium: px,
      large: px,
      extra_large: px,
    });
    setMode("one");
  }, [onChange]);

  const applyMobile = useCallback((px) => {
    onChange({ extra_small: px, small: px });
    setMode("two");
  }, [onChange]);

  const applyDesktop = useCallback((px) => {
    onChange({ medium: px, large: px, extra_large: px });
    setMode("two");
  }, [onChange]);

  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
          {infoText ? <span className="ml-2 align-middle"><InfoIcon text={infoText} /></span> : null}
        </label>
      )}
      {help && <div className={`mb-2 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
      <ScreenSizeModeSelector
        mode={mode}
        onChange={(newMode) => setMode(newMode)}
        disabled={false}
      />

      {mode === "one" ? (
        <FontSizeField
          label=""
          help={null}
          value={value?.extra_small ?? value?.small ?? value?.medium ?? value?.large ?? value?.extra_large ?? 16}
          onChange={setUnified}
          allValues={value}
          labelSize="small"
        />
      ) : mode === "two" ? (
        <div className="grid grid-cols-1 gap-2">
          <FontSizeField label="Phone" help={null} value={vMobile ?? 16} onChange={applyMobile} allValues={value} labelSize="small" />
          <FontSizeField label="Desktop" help={null} value={vDesktop ?? 16} onChange={applyDesktop} allValues={value} labelSize="small" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2">
          <FontSizeField label="Phone" help={null} value={value?.extra_small ?? 16} onChange={(px) => onChange({ extra_small: px })} allValues={value} labelSize="small" />
          <FontSizeField label="Tablet" help={null} value={(value?.small ?? value?.extra_small) ?? 16} onChange={(px) => onChange({ small: px })} allValues={value} labelSize="small" />
          <FontSizeField label="Laptop" help={null} value={value?.medium ?? 16} onChange={(px) => onChange({ medium: px })} allValues={value} labelSize="small" />
          <FontSizeField label="Desktop" help={null} value={(value?.large ?? value?.medium) ?? 16} onChange={(px) => onChange({ large: px })} allValues={value} labelSize="small" />
          <FontSizeField label="Desk XL" help={null} value={(value?.extra_large ?? value?.large) ?? 16} onChange={(px) => onChange({ extra_large: px })} allValues={value} labelSize="small" />
        </div>
      )}
    </div>
  );
}


